import { useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { frFR, enUS } from '@mui/x-data-grid';

import { DataGrid } from '@mui/x-data-grid';
import { GridRowsProp, GridColDef } from '@mui/x-data-grid';
import { DataGridProps } from '@mui/x-data-grid/models/props/DataGridProps';
import sxDataGrid from '../../../../../app/styles/sx/sxDataGrid';
import sxDataGridDark from '../../../../../app/styles/sx/sxDataGridDark';
import { Box } from '@mui/system';
import langInvestFundPerformancesListedAssetsLang from '../../../langs/langInvestFundPerformancesListedAssetsLang';
import IFundPerformancesPerformanceSG from '../../../interfaces/fund/performances/IFundPerformancesPerformanceSG';
import _ from 'lodash';
import i18next from 'i18next';
import { langLookup } from '../../../../../app/utils/langLookup';

type FFundPerformanceDetailsTableProps = Omit<DataGridProps, 'rows' | 'columns'> & {
	performances: IFundPerformancesPerformanceSG[];
	title: string;
	isTranslatable?: boolean;
	color?: 'inherit' | 'primary' | 'secondary';
};

const deconstructIFundPerformancesPerformance = (performances: IFundPerformancesPerformanceSG[]) => {
	const fundsPerf: number[] = [];
	const fundsBench: number[] = [];
	const dateTags: string[] = [];

	for (const performance of performances) {
		fundsPerf.push(performance.value ?? 0);
		fundsBench.push(performance.bench ?? 0);
		dateTags.push(performance.dateType ?? '');
	}

	return { fundsPerf, fundsBench, dateTags };
};

const toPercentFormat = (value?: number) =>
	value === undefined || value === null
		? 'n.c'
		: _.round((value === 0 ? 0 : value) * 100, 2).toLocaleString(i18next.language, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  });

const FFundPerformanceDetailsTable = (props: FFundPerformanceDetailsTableProps) => {
	const { color, performances, title, isTranslatable, ...otherProps } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const { i18n } = useTranslation();

	let sxData = null;
	if (color === 'primary' || theme.palette.mode === 'dark') {
		sxData = {
			...sxDataGrid(theme),
			...sxDataGridDark(),
		};
	} else {
		sxData = sxDataGrid(theme);
	}

	const { fundsPerf, fundsBench, dateTags } = deconstructIFundPerformancesPerformance(performances);

	const maxLength = Math.max(fundsPerf.length, fundsBench.length, dateTags.length);

	const rows: GridRowsProp = [
		{
			id: 1,
			lineTitle: t(langInvestFundPerformancesListedAssetsLang.funds.tag),
			...Array.from({ length: maxLength }).reduce<Record<string, string>>((acc, _, index) => {
				acc[`col${index + 1}`] = toPercentFormat(fundsPerf[index]);
				return acc;
			}, {}),
		},
		{
			id: 2,
			lineTitle: t(langInvestFundPerformancesListedAssetsLang.benchmark.tag),
			col1: toPercentFormat(fundsBench[0]),
			col2: toPercentFormat(fundsBench[1]),
			col3: toPercentFormat(fundsBench[2]),
			col4: toPercentFormat(fundsBench[3]),
			col5: toPercentFormat(fundsBench[4]),
			...Array.from({ length: maxLength }).reduce<Record<string, string>>((acc, _, index) => {
				acc[`col${index + 1}`] = toPercentFormat(fundsBench[index]);
				return acc;
			}, {}),
		},
	];

	const strDateTags = isTranslatable
		? dateTags.map((x) => t(langLookup(langInvestFundPerformancesListedAssetsLang, x)?.tag ?? ''))
		: dateTags;

	const columns: GridColDef[] = [
		{
			field: 'lineTitle',
			headerName: title,
			flex: 1.1,
			sortable: false,
			headerClassName: 'customHeader',
		},
		...Array.from({ length: maxLength }).map((_, index) => ({
			field: `col${index + 1}`,
			headerName: strDateTags[index],
			flex: 1,
			sortable: false,
		})),
	];

	return (
		<Box
			sx={{
				width: '100%',
				'& .funds': {
					backgroundColor: '#39b8b81a',
					borderRadius: '5px',
					'& .MuiDataGrid-cell:first-of-type': {
						color: '#2ABDBB',
						fontWeight: 600,
					},
				},
				'& .others': {
					borderRadius: '5px',
					'& .MuiDataGrid-cell:first-of-type': {
						color: '#6F8098',
						fontWeight: 600,
					},
				},
				'& .customHeader': {
					'& .MuiDataGrid-columnHeaderTitle': {
						color: theme.palette.mode === 'light' ? '#1A2B48' : '#F8F8F8',
					},
				},
			}}
		>
			<DataGrid
				rows={rows}
				headerHeight={45}
				rowHeight={45}
				{...otherProps}
				density="standard"
				sx={{
					...sxData,
					p: 2,
				}}
				localeText={
					i18n.language === 'fr'
						? frFR.components.MuiDataGrid.defaultProps.localeText
						: enUS.components.MuiDataGrid.defaultProps.localeText
				}
				columns={columns}
				getRowClassName={(params) => (params.isFirstVisible ? 'funds' : 'others')}
				hideFooter
				hideFooterPagination
				hideFooterSelectedRowCount
				disableSelectionOnClick
				disableColumnFilter
				disableColumnMenu
				disableColumnSelector
				autoHeight
				pageSize={2}
			/>
		</Box>
	);
};

export default FFundPerformanceDetailsTable;
